import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  bookingService,
  type BookingInfoResponse,
  type CalendarDay,
  type TimeSlot,
  type BookingTreatment,
} from '../../services/bookingService';

/* ================================================================
   CONSTANTS
   ================================================================ */
const WEEKDAYS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

type Step = 'treatment' | 'datetime' | 'details' | 'confirmed';

/* ================================================================
   COLOUR UTILITIES
   ================================================================ */
function hexToRgb(hex: string) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function adjustBrightness(hex: string, pct: number) {
  const { r, g, b } = hexToRgb(hex);
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v + 255 * pct)));
  return `rgb(${c(r)}, ${c(g)}, ${c(b)})`;
}

/* ================================================================
   CSRF HELPER
   ================================================================ */
function getCsrf(): string {
  return (
    document.cookie.split('; ').find((r) => r.startsWith('csrftoken='))?.split('=')[1] ||
    (window as any).CSRF_TOKEN ||
    ''
  );
}

/* ================================================================
   LOGIN MODAL
   ================================================================ */
function LoginModal({ onClose, accent }: { onClose: () => void; accent: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('username', email);
      fd.append('password', password);
      fd.append('csrfmiddlewaretoken', getCsrf());
      const res = await fetch('/accounts/login/', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (res.redirected) {
        window.location.href = '/';
      } else {
        setError('Identifiants incorrects. Veuillez réessayer.');
      }
    } catch {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        style={{ animation: 'fadeIn .2s ease-out' }}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'modalSlideUp .3s cubic-bezier(.16,1,.3,1)' }}
      >
        <div
          className="h-1.5"
          style={{ background: `linear-gradient(90deg, ${accent}, ${adjustBrightness(accent, -0.18)})` }}
        />
        <div className="p-7">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Connexion</h2>
              <p className="text-xs text-slate-400 mt-0.5">Accédez à votre espace praticien</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-300 bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                placeholder="vous@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-300 bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="px-3.5 py-2.5 rounded-lg bg-red-50 border border-red-100 text-red-600 text-xs font-medium">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-40 hover:shadow-lg active:scale-[.98]"
              style={{ backgroundColor: accent }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Connexion…
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   MAIN BOOKING PAGE COMPONENT
   ================================================================ */
const PublicBookingPage: React.FC = () => {
  /* ── state ──────────────────────────────────────────────── */
  const [info, setInfo] = useState<BookingInfoResponse | null>(null);
  const [calendar, setCalendar] = useState<CalendarDay[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loadInfoError, setLoadInfoError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>('treatment');
  const [selectedTreatment, setSelectedTreatment] = useState<BookingTreatment | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmationDetails, setConfirmationDetails] = useState<Record<string, string> | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  /* derived */
  const accent = info?.config?.primary_color || '#0ea5e9';
  const todayStr = new Date().toISOString().slice(0, 10);

  /* ── load practice info + calendar ─────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const [infoRes, calRes] = await Promise.all([
          bookingService.getInfo(),
          bookingService.getCalendar(8),
        ]);
        setInfo(infoRes);
        setCalendar(calRes.dates);
        if (!infoRes.treatments?.length) setStep('datetime');
      } catch (e: any) {
        setLoadInfoError(e.message || 'Unable to load booking information');
      }
    })();
  }, []);

  /* ── load slots when date changes ──────────────────────── */
  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    bookingService
      .getSlots(selectedDate)
      .then((r) => setSlots(r.slots))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  /* ── build calendar grid ───────────────────────────────── */
  const availableDates = useMemo(
    () => new Set(calendar.filter((d) => d.available).map((d) => d.date)),
    [calendar],
  );

  const calendarGrid = useMemo(() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const cells: { day: number; dateStr: string; available: boolean; inMonth: boolean }[] = [];
    for (let i = 0; i < startDow; i++) cells.push({ day: 0, dateStr: '', available: false, inMonth: false });
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, dateStr: ds, available: availableDates.has(ds), inMonth: true });
    }
    return cells;
  }, [calendarMonth, availableDates]);

  const availableSlots = useMemo(() => slots.filter((s) => s.available), [slots]);

  /* ── navigation helpers ────────────────────────────────── */
  const goBack = useCallback(() => {
    if (step === 'datetime') {
      if (info?.treatments?.length) {
        setStep('treatment');
        setSelectedDate(null);
        setSelectedSlot(null);
      }
    } else if (step === 'details') {
      setStep('datetime');
    }
  }, [step, info]);

  const prevMonth = () =>
    setCalendarMonth((p) => (p.month === 0 ? { year: p.year - 1, month: 11 } : { year: p.year, month: p.month - 1 }));
  const nextMonth = () =>
    setCalendarMonth((p) => (p.month === 11 ? { year: p.year + 1, month: 0 } : { year: p.year, month: p.month + 1 }));

  /* ── submit ─────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await bookingService.createBooking({
        date: selectedDate,
        time: selectedSlot,
        treatment_id: selectedTreatment?.id,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email,
        notes: form.notes || undefined,
      });
      if (res.success) {
        setConfirmationDetails(res.details as any);
        setStep('confirmed');
      } else {
        setSubmitError(res.error || 'Booking failed');
      }
    } catch (e: any) {
      setSubmitError(e.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── format helpers ─────────────────────────────────────── */
  const formatDateLong = (ds: string) => {
    const d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateShort = (ds: string) => {
    const d = new Date(ds + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const stepLabels = info?.treatments?.length
    ? ['Soin', 'Date & Heure', 'Informations']
    : ['Date & Heure', 'Informations'];
  const stepIndex =
    step === 'treatment' ? 0 : step === 'datetime' ? (info?.treatments?.length ? 1 : 0) : step === 'details' ? (info?.treatments?.length ? 2 : 1) : 3;

  /* ================================================================
     RENDER
     ================================================================ */

  /* loading / error states */
  if (loadInfoError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#f8fafc' }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-10 max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Réservation en ligne indisponible</h2>
          <p className="text-slate-400 text-sm">{loadInfoError}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#f8fafc' }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-[3px] border-slate-200 rounded-full animate-spin" style={{ borderTopColor: accent }} />
          <p className="text-slate-400 text-sm tracking-wide">Chargement…</p>
        </div>
      </div>
    );
  }

  const practice = info.practice;
  const config = info.config;

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#f8fafc' }}>
      {/* ── FONTS ──────────────────────────────────────────── */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ── HEADER ────────────────────────────────────────── */}
      <header
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${adjustBrightness(accent, -0.2)} 100%)`,
        }}
      >
        {/* decorative shapes */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/[.06]" />
        <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-white/[.04]" />
        <div className="absolute top-1/2 left-1/3 w-32 h-32 rounded-full bg-white/[.03]" />

        <div className="relative max-w-5xl mx-auto px-5 py-6 sm:py-8">
          {/* top bar */}
          <div className="flex items-center justify-between mb-5">
            <span className="text-white/40 text-xs font-semibold tracking-widest uppercase">Réservation en ligne</span>
            <button
              onClick={() => setShowLoginModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-all"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Connexion
            </button>
          </div>

          {/* practice info */}
          <div className="flex items-center gap-5">
            {practice.logo_url && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden shadow-lg bg-white/20 backdrop-blur-sm flex-shrink-0">
                <img src={practice.logo_url} alt="" className="w-full h-full object-contain p-1.5" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight truncate">
                {config.page_title || practice.name}
              </h1>
              {practice.doctor_name && (
                <p className="text-white/80 mt-0.5 text-sm sm:text-base font-semibold">{practice.doctor_name}</p>
              )}
              {practice.specialties && (
                <p className="text-white/50 text-xs mt-1 truncate">
                  {Array.isArray(practice.specialties) ? practice.specialties.join(' · ') : practice.specialties}
                </p>
              )}
              {practice.address && (
                <p className="text-white/40 text-xs mt-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{practice.address}{practice.city ? `, ${practice.city}` : ''}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ──────────────────────────────────── */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-5 py-6 sm:py-8">
        {/* welcome message */}
        {config.welcome_message && step !== 'confirmed' && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm px-5 py-3.5 mb-5 text-center text-slate-500 text-sm">
            {config.welcome_message}
          </div>
        )}

        {/* step indicator */}
        {step !== 'confirmed' && (
          <div className="flex items-center justify-center gap-1.5 mb-7">
            {stepLabels.map((label, i) => {
              const isActive = i === stepIndex;
              const isCompleted = i < stepIndex;
              return (
                <React.Fragment key={label}>
                  {i > 0 && (
                    <div
                      className="h-[2px] w-8 sm:w-12 rounded-full transition-colors duration-300"
                      style={{ backgroundColor: isCompleted ? accent : '#e2e8f0' }}
                    />
                  )}
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all duration-300"
                      style={{
                        backgroundColor: isActive || isCompleted ? accent : '#e2e8f0',
                        color: isActive || isCompleted ? '#fff' : '#94a3b8',
                        boxShadow: isActive ? `0 0 0 3px ${adjustBrightness(accent, 0.4)}` : 'none',
                      }}
                    >
                      {isCompleted ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span
                      className="hidden sm:inline text-xs font-semibold transition-colors"
                      style={{ color: isActive ? accent : isCompleted ? accent : '#94a3b8' }}
                    >
                      {label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── STEP: Treatment ──────────────────────────────── */}
        {step === 'treatment' && info.treatments.length > 0 && (
          <section style={{ animation: 'fadeUp .35s ease-out both' }}>
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-slate-800">Choisir un soin</h2>
              <p className="text-slate-400 text-sm mt-1">Sélectionnez le type de rendez-vous souhaité</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 max-w-2xl mx-auto">
              {info.treatments.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setSelectedTreatment(t);
                    setStep('datetime');
                  }}
                  className="group relative bg-white rounded-xl border-2 border-slate-100 p-5 text-left transition-all duration-200 hover:shadow-md hover:border-slate-200"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: t.color || accent }} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-800 group-hover:text-slate-900 truncate">{t.name}</h3>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                        {t.duration > 0 && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t.duration} min
                          </span>
                        )}
                        {t.cost && parseFloat(t.cost) > 0 && (
                          <span className="font-semibold text-slate-500">{parseFloat(t.cost).toFixed(0)} MAD</span>
                        )}
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-slate-200 group-hover:text-slate-400 transition-colors flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── STEP: Date & Time (combined Calendly-style) ──── */}
        {step === 'datetime' && (
          <section style={{ animation: 'fadeUp .35s ease-out both' }}>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                {/* LEFT PANEL — Practice / Treatment info */}
                <div className="lg:w-72 xl:w-80 border-b lg:border-b-0 lg:border-r border-slate-100 p-6 flex flex-col">
                  {info?.treatments?.length ? (
                    <button
                      onClick={goBack}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors mb-5 self-start"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Retour
                    </button>
                  ) : null}

                  {practice.logo_url ? (
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-50 border border-slate-100 mb-4 flex-shrink-0">
                      <img src={practice.logo_url} alt="" className="w-full h-full object-contain p-1" />
                    </div>
                  ) : (
                    <div
                      className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                      style={{ backgroundColor: accent }}
                    >
                      {(practice.doctor_name || practice.name || 'D').charAt(0)}
                    </div>
                  )}

                  <p className="text-xs text-slate-400 font-medium">{practice.doctor_name}</p>
                  <h3 className="text-base font-bold text-slate-800 mt-0.5">{practice.name}</h3>

                  {selectedTreatment && (
                    <div className="mt-5 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: selectedTreatment.color || accent }} />
                        <span className="text-sm font-semibold text-slate-700">{selectedTreatment.name}</span>
                      </div>
                      {selectedTreatment.duration > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {selectedTreatment.duration} min
                        </div>
                      )}
                    </div>
                  )}

                  {practice.address && (
                    <div className="mt-auto pt-5">
                      <div className="flex items-start gap-1.5 text-xs text-slate-400">
                        <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{practice.address}{practice.city ? `, ${practice.city}` : ''}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT PANEL — Calendar + Time slots */}
                <div className="flex-1 p-6">
                  <h2 className="text-base font-bold text-slate-800 mb-5">Choisir une date et un horaire</h2>

                  {/* month navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button onClick={prevMonth} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors">
                      <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h3 className="text-sm font-bold text-slate-800">
                      {MONTHS[calendarMonth.month]} {calendarMonth.year}
                    </h3>
                    <button onClick={nextMonth} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors">
                      <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* weekday headers */}
                  <div className="grid grid-cols-7 mb-1">
                    {WEEKDAYS.map((d) => (
                      <div key={d} className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wider py-1.5">
                        {d}
                      </div>
                    ))}
                  </div>

                  {/* calendar grid — compact cells, no aspect-square */}
                  <div className="grid grid-cols-7">
                    {calendarGrid.map((cell, i) => {
                      if (!cell.inMonth) return <div key={i} />;
                      const isSelected = cell.dateStr === selectedDate;
                      const isToday = cell.dateStr === todayStr;
                      const isAvailable = cell.available;
                      return (
                        <div key={i} className="flex items-center justify-center py-[3px]">
                          <button
                            disabled={!isAvailable}
                            onClick={() => {
                              setSelectedDate(cell.dateStr);
                              setSelectedSlot(null);
                            }}
                            className={[
                              'w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 relative',
                              isSelected ? 'text-white font-bold' : '',
                              isAvailable && !isSelected ? 'text-slate-800 font-semibold hover:bg-slate-100 cursor-pointer' : '',
                              !isAvailable ? 'text-slate-300 cursor-default' : '',
                            ].join(' ')}
                            style={isSelected ? { backgroundColor: accent, boxShadow: `0 2px 8px ${accent}50` } : undefined}
                          >
                            {cell.day}
                            {isToday && !isSelected && (
                              <span
                                className="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                style={{ backgroundColor: accent }}
                              />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* timezone */}
                  <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                  </div>

                  {/* time slots */}
                  {selectedDate && (
                    <div className="mt-5 pt-5 border-t border-slate-100" style={{ animation: 'fadeUp .25s ease-out both' }}>
                      <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                        {formatDateShort(selectedDate)}
                      </h4>

                      {slotsLoading ? (
                        <div className="py-8 flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-2 border-slate-200 rounded-full animate-spin" style={{ borderTopColor: accent }} />
                          <p className="text-slate-400 text-xs">Chargement…</p>
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-slate-400 text-sm">Aucun créneau disponible</p>
                          <p className="text-slate-300 text-xs mt-1">Veuillez choisir une autre date</p>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                            {availableSlots.map((s) => {
                              const isSel = selectedSlot === s.start;
                              return (
                                <button
                                  key={s.start}
                                  onClick={() => setSelectedSlot(s.start)}
                                  className={[
                                    'py-2.5 rounded-lg text-sm font-semibold border transition-all duration-200',
                                    isSel ? 'text-white border-transparent' : 'border-slate-200 text-slate-700 hover:border-current',
                                  ].join(' ')}
                                  style={
                                    isSel
                                      ? { backgroundColor: accent, boxShadow: `0 2px 8px ${accent}40` }
                                      : undefined
                                  }
                                  onMouseEnter={(e) => {
                                    if (!isSel) (e.currentTarget.style.borderColor = accent);
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSel) (e.currentTarget.style.borderColor = '#e2e8f0');
                                  }}
                                >
                                  {s.start}
                                </button>
                              );
                            })}
                          </div>
                          {selectedSlot && (
                            <button
                              onClick={() => setStep('details')}
                              className="mt-5 w-full py-3 rounded-xl text-white text-sm font-bold transition-all hover:shadow-lg active:scale-[.98]"
                              style={{ backgroundColor: accent }}
                            >
                              Continuer
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── STEP: Details (patient form) ─────────────────── */}
        {step === 'details' && (
          <section style={{ animation: 'fadeUp .35s ease-out both' }}>
            <div className="flex items-center justify-between mb-6">
              <button onClick={goBack} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour
              </button>
              <h2 className="text-lg font-bold text-slate-800">Vos informations</h2>
              <div className="w-14" />
            </div>

            <div className="grid gap-5 lg:grid-cols-5">
              {/* summary sidebar */}
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sticky top-6">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-4">Résumé</h3>

                  {selectedTreatment && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: selectedTreatment.color || accent }} />
                      <span className="text-sm text-slate-700 font-semibold truncate">{selectedTreatment.name}</span>
                    </div>
                  )}

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2.5 text-slate-500">
                      <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{selectedDate && formatDateLong(selectedDate)}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-slate-500">
                      <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-semibold">{selectedSlot}</span>
                    </div>
                    {practice.doctor_name && (
                      <div className="flex items-center gap-2.5 text-slate-500">
                        <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>{practice.doctor_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* form */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                  {submitError && (
                    <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">{submitError}</div>
                  )}
                  {config.booking_instructions && (
                    <p className="text-slate-400 text-sm mb-5">{config.booking_instructions}</p>
                  )}

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Prénom *</label>
                        <input
                          type="text"
                          value={form.first_name}
                          onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                          style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                          placeholder="Prénom"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Nom *</label>
                        <input
                          type="text"
                          value={form.last_name}
                          onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                          style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                          placeholder="Nom"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                        Téléphone {config.require_phone ? '*' : ''}
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                        style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                        placeholder="+212 6XX XXX XXX"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                        Email {config.require_email ? '*' : ''}
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                        style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Notes (optionnel)</label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow resize-none"
                        style={{ '--tw-ring-color': `${accent}30` } as React.CSSProperties}
                        placeholder="Informations complémentaires…"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={
                      submitting ||
                      !form.first_name ||
                      !form.last_name ||
                      (config.require_phone && !form.phone) ||
                      (config.require_email && !form.email)
                    }
                    className="mt-6 w-full py-3 rounded-xl text-white font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg active:scale-[.98]"
                    style={{ backgroundColor: accent }}
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Confirmation…
                      </span>
                    ) : (
                      'Confirmer le rendez-vous'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── STEP: Confirmed ─────────────────────────────── */}
        {step === 'confirmed' && (
          <section style={{ animation: 'fadeUp .35s ease-out both' }} className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: adjustBrightness(accent, 0.42) }}
              >
                <svg className="w-10 h-10" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-xl font-extrabold text-slate-800 mb-1.5">Rendez-vous confirmé !</h2>
              <p className="text-slate-400 text-sm mb-6">
                {config.confirmation_message || 'Votre rendez-vous a été réservé avec succès. À bientôt !'}
              </p>

              {confirmationDetails && (
                <div className="rounded-xl border border-slate-100 divide-y divide-slate-50 text-sm text-left mb-6 overflow-hidden">
                  {confirmationDetails.treatment && (
                    <div className="px-5 py-3 flex justify-between items-center">
                      <span className="text-slate-400">Soin</span>
                      <span className="text-slate-700 font-semibold">{confirmationDetails.treatment}</span>
                    </div>
                  )}
                  <div className="px-5 py-3 flex justify-between items-center">
                    <span className="text-slate-400">Date</span>
                    <span className="text-slate-700 font-semibold">{confirmationDetails.date}</span>
                  </div>
                  <div className="px-5 py-3 flex justify-between items-center">
                    <span className="text-slate-400">Heure</span>
                    <span className="text-slate-700 font-semibold">{confirmationDetails.time}</span>
                  </div>
                  <div className="px-5 py-3 flex justify-between items-center">
                    <span className="text-slate-400">Médecin</span>
                    <span className="text-slate-700 font-semibold">{confirmationDetails.doctor}</span>
                  </div>
                  <div className="px-5 py-3 flex justify-between items-center">
                    <span className="text-slate-400">Statut</span>
                    <span className="font-semibold capitalize" style={{ color: accent }}>{confirmationDetails.status}</span>
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  setStep(info?.treatments?.length ? 'treatment' : 'datetime');
                  setSelectedTreatment(null);
                  setSelectedDate(null);
                  setSelectedSlot(null);
                  setForm({ first_name: '', last_name: '', phone: '', email: '', notes: '' });
                  setConfirmationDetails(null);
                }}
                className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all hover:shadow-md"
                style={{ color: accent, backgroundColor: adjustBrightness(accent, 0.42) }}
              >
                Nouveau rendez-vous
              </button>
            </div>
          </section>
        )}
      </main>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 bg-white mt-auto">
        <div className="max-w-5xl mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-400">
          <p>&copy; {new Date().getFullYear()} {practice.name}</p>
          <p>
            Propulsé par{' '}
            <span className="font-bold text-slate-500">Doctif</span>
          </p>
        </div>
      </footer>

      {/* ── LOGIN MODAL ─────────────────────────────────── */}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} accent={accent} />}

      {/* ── STYLES ──────────────────────────────────────── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PublicBookingPage;
